import type { SelfTestMode, LedWallMode, GridOffset, PixelMapMode } from './types'

interface TextElement {
  id: string
  text: string
  x: number
  y: number
  fontSize: number
  fontFamily: string
  fontWeight: number
  fontStyle: string
  textAlign: string
  color: string
  strokeWidth?: number
}

interface LogoElement {
  id: string
  image: HTMLImageElement
  x: number
  y: number
  width: number
  height: number
  originalWidth: number
  originalHeight: number
  opacity: number
  isGif?: boolean
  imageSrc?: string
}

export function drawSelectionIndicator(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  isDragging: boolean
) {
  const padding = 8
  const rectPadding = padding + 5

  ctx.fillStyle = "rgba(0, 255, 0, 0.1)"
  ctx.fillRect(x - rectPadding, y - rectPadding, w + rectPadding * 2, h + rectPadding * 2)

  ctx.strokeStyle = isDragging ? "#00ff00" : "#00cc00"
  ctx.lineWidth = isDragging ? 3 : 2
  ctx.setLineDash(isDragging ? [10, 5] : [5, 5])
  ctx.strokeRect(x - rectPadding, y - rectPadding, w + rectPadding * 2, h + rectPadding * 2)
  ctx.setLineDash([])

  const cornerSize = 8
  ctx.fillStyle = "#00ff00"
  // Top-left
  ctx.fillRect(x - rectPadding, y - rectPadding, cornerSize, 3)
  ctx.fillRect(x - rectPadding, y - rectPadding, 3, cornerSize)
  // Top-right
  ctx.fillRect(x + w + rectPadding - cornerSize, y - rectPadding, cornerSize, 3)
  ctx.fillRect(x + w + rectPadding - 3, y - rectPadding, 3, cornerSize)
  // Bottom-left
  ctx.fillRect(x - rectPadding, y + h + rectPadding - 3, cornerSize, 3)
  ctx.fillRect(x - rectPadding, y + h + rectPadding - cornerSize, 3, cornerSize)
  // Bottom-right
  ctx.fillRect(x + w + rectPadding - cornerSize, y + h + rectPadding - 3, cornerSize, 3)
  ctx.fillRect(x + w + rectPadding - 3, y + h + rectPadding - cornerSize, 3, cornerSize)
}

export function renderGrid(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  config: {
    showGrid: boolean
    showLEDNumbers: boolean
    gridSize: number
    gridOffset: GridOffset
    ledWallMode?: LedWallMode
    gridEvenOpacity?: number
    gridOddOpacity?: number
  },
  selfTestMode: SelfTestMode,
  cyanPanels: Set<string>
) {
  if (!config.showGrid) return

  const offsetX = config.gridOffset.x
  const offsetY = config.gridOffset.y
  const evenOpacity = (config.gridEvenOpacity ?? 97) / 100
  const oddOpacity = (config.gridOddOpacity ?? 85) / 100

  if (config.ledWallMode?.enabled) {
    const { columns, rows, panelWidth, panelHeight, borderWidth } = config.ledWallMode
    const gridLineWidth = borderWidth

    for (let col = 0; col < columns; col++) {
      for (let row = 0; row < rows; row++) {
        const x = col * panelWidth + offsetX
        const y = row * panelHeight + offsetY
        const cellWidth = panelWidth - gridLineWidth
        const cellHeight = panelHeight - gridLineWidth

        if (cellWidth > 0 && cellHeight > 0) {
          if (selfTestMode.enabled) {
            const panelKey = `${col + 1},${row + 1}`
            const isActivePanel = selfTestMode.activePanels.has(panelKey)

            if (selfTestMode.showBorders) {
              ctx.strokeStyle = '#FFFFFF'
              ctx.lineWidth = gridLineWidth
              ctx.strokeRect(x, y, panelWidth, panelHeight)
            }

            if (isActivePanel) {
              ctx.fillStyle = '#FFFFFF'
              if (selfTestMode.showBorders) {
                ctx.fillRect(x + gridLineWidth / 2, y + gridLineWidth / 2, cellWidth, cellHeight)
              } else {
                ctx.fillRect(x, y, panelWidth, panelHeight)
              }
            }
          } else {
            const isEven = (row + col) % 2 === 0
            const opacity = isEven ? evenOpacity : oddOpacity
            ctx.fillStyle = `rgba(0, 0, 0, ${opacity})`
            ctx.fillRect(x + gridLineWidth / 2, y + gridLineWidth / 2, cellWidth, cellHeight)
          }
        }
      }
    }
  } else if (!config.showLEDNumbers) {
    const gridLineWidth = config.ledWallMode?.borderWidth ?? 2
    const gridSize = config.gridSize

    for (let x = offsetX; x < width; x += gridSize) {
      for (let y = offsetY; y < height; y += gridSize) {
        const isLeftEdge = x === offsetX
        const isTopEdge = y === offsetY
        const offsetLeft = isLeftEdge ? gridLineWidth / 2 : gridLineWidth
        const offsetTop = isTopEdge ? gridLineWidth / 2 : gridLineWidth
        const cellWidth = Math.min(gridSize - offsetLeft, width - x - offsetLeft)
        const cellHeight = Math.min(gridSize - offsetTop, height - y - offsetTop)

        if (cellWidth > 0 && cellHeight > 0) {
          const row = Math.floor((y - offsetY) / gridSize)
          const col = Math.floor((x - offsetX) / gridSize)
          const isEven = (row + col) % 2 === 0
          const opacity = isEven ? evenOpacity : oddOpacity
          ctx.fillStyle = `rgba(0, 0, 0, ${opacity})`
          ctx.fillRect(x + offsetLeft, y + offsetTop, cellWidth, cellHeight)
        }
      }
    }
  } else {
    const gridSize = config.gridSize
    const panelsX = Math.ceil(width / gridSize)
    const panelsY = Math.ceil(height / gridSize)
    const gridLineWidth = config.ledWallMode?.borderWidth ?? 2

    for (let row = 0; row < panelsY; row++) {
      for (let col = 0; col < panelsX; col++) {
        const x = col * gridSize + offsetX
        const y = row * gridSize + offsetY
        const panelWidth = Math.min(gridSize, width - x)
        const panelHeight = Math.min(gridSize, height - y)

        if (panelWidth > 0 && panelHeight > 0) {
          const isLeftEdge = x === offsetX
          const isTopEdge = y === offsetY
          const offsetLeft = isLeftEdge ? gridLineWidth / 2 : gridLineWidth
          const offsetTop = isTopEdge ? gridLineWidth / 2 : gridLineWidth
          const cellX = x + offsetLeft
          const cellY = y + offsetTop
          const cellWidth = panelWidth - offsetLeft
          const cellHeight = panelHeight - offsetTop

          if (cellWidth > 0 && cellHeight > 0) {
            const isEven = (row + col) % 2 === 0
            const opacity = isEven ? evenOpacity : oddOpacity
            ctx.fillStyle = `rgba(0, 0, 0, ${opacity})`
            ctx.fillRect(cellX, cellY, cellWidth, cellHeight)
          }
        }
      }
    }

    ctx.shadowColor = "transparent"
    ctx.shadowBlur = 0
    ctx.shadowOffsetX = 0
    ctx.shadowOffsetY = 0
  }
}

export function renderCyanPanels(
  ctx: CanvasRenderingContext2D,
  selfTestMode: SelfTestMode,
  ledWallMode: LedWallMode,
  gridOffset: GridOffset,
  cyanPanels: Set<string>,
  subdivisionMode: boolean,
  redZones: Set<string>,
  selectedQuadrant: 'tl' | 'tr' | 'bl' | 'br'
) {
  if (!selfTestMode.enabled || !ledWallMode.enabled || cyanPanels.size === 0) return

  const { columns, rows, panelWidth, panelHeight } = ledWallMode
  const offsetX = gridOffset.x
  const offsetY = gridOffset.y

  cyanPanels.forEach(panelKey => {
    const [x, y] = panelKey.split(',').map(Number)
    const panelCol = x - 1
    const panelRow = y - 1

    if (panelCol >= 0 && panelCol < columns && panelRow >= 0 && panelRow < rows) {
      const panelX = panelCol * panelWidth + offsetX
      const panelY = panelRow * panelHeight + offsetY

      ctx.fillStyle = '#00FFFF'
      if (selfTestMode.showBorders) {
        const cellWidth = panelWidth - 1
        const cellHeight = panelHeight - 1
        ctx.fillRect(panelX + 0.5, panelY + 0.5, cellWidth, cellHeight)
      } else {
        ctx.fillRect(panelX, panelY, panelWidth, panelHeight)
      }

      if (subdivisionMode) {
        const borderOffset = selfTestMode.showBorders ? 0.5 : 0
        const actualWidth = selfTestMode.showBorders ? panelWidth - 1 : panelWidth
        const actualHeight = selfTestMode.showBorders ? panelHeight - 1 : panelHeight
        const baseX = panelX + borderOffset
        const baseY = panelY + borderOffset
        const halfWidth = actualWidth / 2
        const halfHeight = actualHeight / 2

        ctx.strokeStyle = '#333333'
        ctx.lineWidth = 1
        ctx.beginPath()
        ctx.moveTo(baseX + halfWidth, baseY)
        ctx.lineTo(baseX + halfWidth, baseY + actualHeight)
        ctx.moveTo(baseX, baseY + halfHeight)
        ctx.lineTo(baseX + actualWidth, baseY + halfHeight)
        ctx.stroke()

        ;(['tl', 'tr', 'bl', 'br'] as const).forEach(quadrant => {
          const zoneKey = `${x},${y},${quadrant}`
          if (redZones.has(zoneKey)) {
            ctx.fillStyle = '#FF0000'
            let qx: number | undefined, qy: number | undefined
            switch (quadrant) {
              case 'tl': qx = baseX; qy = baseY; break
              case 'tr': qx = baseX + halfWidth; qy = baseY; break
              case 'bl': qx = baseX; qy = baseY + halfHeight; break
              case 'br': qx = baseX + halfWidth; qy = baseY + halfHeight; break
            }
            if (qx !== undefined && qy !== undefined) {
              ctx.fillRect(qx, qy, halfWidth, halfHeight)
            }
          }
        })

        if (panelKey === `${selfTestMode.selectedPanelX},${selfTestMode.selectedPanelY}`) {
          ctx.strokeStyle = '#FFFF00'
          ctx.lineWidth = 2
          let qx: number | undefined, qy: number | undefined
          switch (selectedQuadrant) {
            case 'tl': qx = baseX; qy = baseY; break
            case 'tr': qx = baseX + halfWidth; qy = baseY; break
            case 'bl': qx = baseX; qy = baseY + halfHeight; break
            case 'br': qx = baseX + halfWidth; qy = baseY + halfHeight; break
          }
          if (qx !== undefined && qy !== undefined) {
            ctx.strokeRect(qx, qy, halfWidth, halfHeight)
          }
        }
      }
    }
  })
}

export function renderSelfTestLEDNumbers(
  ctx: CanvasRenderingContext2D,
  selfTestMode: SelfTestMode,
  ledWallMode: LedWallMode,
  gridOffset: GridOffset,
  cyanPanels: Set<string>,
  subdivisionMode: boolean,
  redZones: Set<string>,
  selectedQuadrant: 'tl' | 'tr' | 'bl' | 'br',
  isAnimating: boolean,
  startTime: number
) {
  if (!selfTestMode.enabled || !ledWallMode.enabled) return

  const { columns, rows, panelWidth, panelHeight } = ledWallMode
  const fontSize = Math.max(16, Math.min(panelWidth / 4, panelHeight / 4, 48))
  const offsetX = gridOffset.x
  const offsetY = gridOffset.y

  ctx.font = `bold ${fontSize}px Arial, sans-serif`
  ctx.textAlign = "center"
  ctx.textBaseline = "middle"

  let showNumbers = true

  if (isAnimating) {
    const cycleTime = (Date.now() - startTime) % 10000
    if (cycleTime >= 5000) {
      showNumbers = false
    }
  }

  if (showNumbers) {
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < columns; col++) {
        const panelKey = `${col + 1},${row + 1}`
        if (selfTestMode.activePanels.has(panelKey) || cyanPanels.has(panelKey)) {
          const x = col * panelWidth + panelWidth / 2 + offsetX
          const y = row * panelHeight + panelHeight / 2 + offsetY
          const panelNumber = `${row + 1}.${col + 1}`

          if (cyanPanels.has(panelKey)) {
            ctx.fillStyle = "rgba(255, 255, 255, 1)"
            ctx.shadowColor = "rgba(0, 0, 0, 0.8)"
          } else {
            ctx.fillStyle = "rgba(0, 0, 0, 1)"
            ctx.shadowColor = "rgba(255, 255, 255, 0.8)"
          }

          ctx.shadowBlur = 2
          ctx.shadowOffsetX = 1
          ctx.shadowOffsetY = 1

          ctx.fillText(panelNumber, x, y)
        }
      }
    }
  }

  if (showNumbers && subdivisionMode && redZones.size > 0) {
    redZones.forEach(zoneKey => {
      const [panelX, panelY, quadrant] = zoneKey.split(',')
      const x = parseInt(panelX)
      const y = parseInt(panelY)
      const panelCol = x - 1
      const panelRow = y - 1

      if (panelCol >= 0 && panelCol < columns && panelRow >= 0 && panelRow < rows) {
        const basePanelX = panelCol * panelWidth + offsetX
        const basePanelY = panelRow * panelHeight + offsetY
        const panelNumber = `${panelRow + 1}.${panelCol + 1}`

        const borderOffset = selfTestMode.showBorders ? 0.5 : 0
        const actualWidth = selfTestMode.showBorders ? panelWidth - 1 : panelWidth
        const actualHeight = selfTestMode.showBorders ? panelHeight - 1 : panelHeight
        const baseX = basePanelX + borderOffset
        const baseY = basePanelY + borderOffset
        const halfWidth = actualWidth / 2
        const halfHeight = actualHeight / 2

        let quadrantCenterX: number | undefined, quadrantCenterY: number | undefined
        switch (quadrant) {
          case 'tl':
            quadrantCenterX = baseX + halfWidth / 2
            quadrantCenterY = baseY + halfHeight / 2
            break
          case 'tr':
            quadrantCenterX = baseX + halfWidth + halfWidth / 2
            quadrantCenterY = baseY + halfHeight / 2
            break
          case 'bl':
            quadrantCenterX = baseX + halfWidth / 2
            quadrantCenterY = baseY + halfHeight + halfHeight / 2
            break
          case 'br':
            quadrantCenterX = baseX + halfWidth + halfWidth / 2
            quadrantCenterY = baseY + halfHeight + halfHeight / 2
            break
        }

        const quadrantFontSize = Math.max(12, fontSize * 0.7)
        ctx.font = `bold ${quadrantFontSize}px Arial, sans-serif`
        ctx.fillStyle = "rgba(255, 255, 255, 1)"
        ctx.shadowColor = "rgba(0, 0, 0, 0.8)"
        ctx.shadowBlur = 2
        ctx.shadowOffsetX = 1
        ctx.shadowOffsetY = 1

        if (quadrantCenterX !== undefined && quadrantCenterY !== undefined) {
          ctx.fillText(panelNumber, quadrantCenterX, quadrantCenterY)
        }

        ctx.font = `bold ${fontSize}px Arial, sans-serif`
      }
    })
  }

  ctx.shadowColor = "transparent"
  ctx.shadowBlur = 0
  ctx.shadowOffsetX = 0
  ctx.shadowOffsetY = 0
}

export function renderStandardLEDNumbers(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  config: {
    showLEDNumbers: boolean
    gridSize: number
    gridOffset: GridOffset
    ledWallMode?: LedWallMode
    patternType: string
  },
  selfTestEnabled: boolean,
  enableAnamorphosis = false
) {
  if (!config.showLEDNumbers || config.patternType === "snake" || selfTestEnabled) return

  const offsetX = config.gridOffset.x
  const offsetY = config.gridOffset.y

  if (config.ledWallMode?.enabled) {
    const { columns, rows, panelWidth, panelHeight } = config.ledWallMode
    const fontSize = Math.max(16, Math.min(panelWidth / 4, panelHeight / 4, 48))

    ctx.font = `bold ${fontSize}px Arial, sans-serif`
    ctx.textAlign = "center"
    ctx.textBaseline = "middle"
    ctx.fillStyle = "rgba(255, 255, 255, 1)"
    ctx.shadowColor = "rgba(0, 0, 0, 0.8)"
    ctx.shadowBlur = 2
    ctx.shadowOffsetX = 1
    ctx.shadowOffsetY = 1

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < columns; col++) {
        const x = col * panelWidth + panelWidth / 2 + offsetX
        const y = row * panelHeight + panelHeight / 2 + offsetY
        const panelNumber = `${row + 1}.${col + 1}`
        if (enableAnamorphosis) {
          ctx.save()
          ctx.translate(x, y)
          ctx.scale(1, 0.5)
          ctx.fillText(panelNumber, 0, 0)
          ctx.restore()
        } else {
          ctx.fillText(panelNumber, x, y)
        }
      }
    }

    ctx.shadowColor = "transparent"
    ctx.shadowBlur = 0
    ctx.shadowOffsetX = 0
    ctx.shadowOffsetY = 0
  } else {
    const gridSize = config.gridSize
    const panelsX = Math.ceil(width / gridSize)
    const panelsY = Math.ceil(height / gridSize)
    const fontSize = Math.max(12, Math.min(gridSize / 4, 48))

    ctx.font = `bold ${fontSize}px Arial, sans-serif`
    ctx.textAlign = "center"
    ctx.textBaseline = "middle"
    ctx.fillStyle = "rgba(255, 255, 255, 1)"
    ctx.shadowColor = "rgba(0, 0, 0, 0.8)"
    ctx.shadowBlur = 2
    ctx.shadowOffsetX = 1
    ctx.shadowOffsetY = 1

    const normalPanelArea = gridSize * gridSize
    const minimumPanelArea = normalPanelArea * 0.45

    for (let row = 0; row < panelsY; row++) {
      for (let col = 0; col < panelsX; col++) {
        const x = col * gridSize + offsetX
        const y = row * gridSize + offsetY
        const panelWidth = Math.min(gridSize, width - x)
        const panelHeight = Math.min(gridSize, height - y)

        if (panelWidth > 0 && panelHeight > 0) {
          const actualPanelArea = panelWidth * panelHeight
          if (actualPanelArea >= minimumPanelArea) {
            const panelNumber = `${row + 1}.${col + 1}`
            const centerX = x + panelWidth / 2
            const centerY = y + panelHeight / 2
            if (enableAnamorphosis) {
              ctx.save()
              ctx.translate(centerX, centerY)
              ctx.scale(1, 0.5)
              ctx.fillText(panelNumber, 0, 0)
              ctx.restore()
            } else {
              ctx.fillText(panelNumber, centerX, centerY)
            }
          }
        }
      }
    }

    ctx.shadowColor = "transparent"
    ctx.shadowBlur = 0
    ctx.shadowOffsetX = 0
    ctx.shadowOffsetY = 0
  }
}

export function renderClassicSonar(
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  canvasHeight: number,
  config: {
    showSonar: boolean
    sonarRevealMode: boolean
    sonarSpeed: number
    gridOffset: GridOffset
    sonarThickness?: number
  },
  elapsedTime: number,
  isExporting: boolean,
  selfTestEnabled: boolean
) {
  if (config.sonarRevealMode || !config.showSonar || selfTestEnabled || (elapsedTime <= 0 && !isExporting)) return

  const centerX = canvasWidth / 2 + config.gridOffset.x
  const centerY = canvasHeight / 2 + config.gridOffset.y
  const maxRadius = Math.max(
    Math.sqrt(centerX * centerX + centerY * centerY),
    Math.sqrt((canvasWidth - centerX) * (canvasWidth - centerX) + centerY * centerY),
    Math.sqrt(centerX * centerX + (canvasHeight - centerY) * (canvasHeight - centerY)),
    Math.sqrt((canvasWidth - centerX) * (canvasWidth - centerX) + (canvasHeight - centerY) * (canvasHeight - centerY))
  )

  const sonarInterval = 4000
  const effectiveElapsedTime = isExporting && elapsedTime === 0
    ? (sonarInterval * 0.01) / config.sonarSpeed
    : elapsedTime
  const sonarTime = (effectiveElapsedTime * config.sonarSpeed) % sonarInterval
  const sonarProgress = sonarTime / sonarInterval

  const sonarRings: { radius: number; opacity: number; progress: number }[] = []
  for (let i = 0; i < 3; i++) {
    const ringProgress = (sonarProgress + i * 0.33) % 1
    if (ringProgress < 0.8) {
      const radius = ringProgress * maxRadius * 1.2
      const fadeProgress = ringProgress * 1.25
      const opacity = fadeProgress < 1 ? Math.max(0, 1 - fadeProgress) : 0
      sonarRings.push({ radius, opacity, progress: ringProgress })
    }
  }

  if (sonarRings.length > 0) {
    const offscreenCanvas = document.createElement('canvas')
    offscreenCanvas.width = canvasWidth
    offscreenCanvas.height = canvasHeight
    const offscreenCtx = offscreenCanvas.getContext('2d')

    if (offscreenCtx) {
      const thickness = config.sonarThickness ?? 4

      sonarRings.forEach((ring) => {
        if (ring.opacity > 0) {
          offscreenCtx.strokeStyle = `rgba(255, 255, 255, ${ring.opacity})`
          offscreenCtx.lineWidth = thickness
          offscreenCtx.beginPath()
          offscreenCtx.arc(centerX, centerY, ring.radius, 0, 2 * Math.PI)
          offscreenCtx.stroke()
        }
      })

      ctx.save()
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.globalCompositeOperation = "difference"
      ctx.drawImage(offscreenCanvas, 0, 0)
      ctx.restore()
    }
  }
}

export function renderLogoElements(
  ctx: CanvasRenderingContext2D,
  logoElements: LogoElement[],
  selectedLogoId: string | null,
  isFullscreen: boolean,
  isDragging: boolean,
  showSelection: boolean,
  selfTestEnabled: boolean,
  enableAnamorphosis = false
) {
  if (selfTestEnabled) return

  logoElements.forEach((logoElement) => {
    if (logoElement.isGif) {
      if (showSelection && !isFullscreen && selectedLogoId === logoElement.id) {
        ctx.save()
        ctx.globalAlpha = 1
        drawSelectionIndicator(ctx, logoElement.x, logoElement.y, logoElement.width, logoElement.height, isDragging)
        ctx.restore()
      }
      return
    }

    ctx.save()
    ctx.globalAlpha = logoElement.opacity
    if (enableAnamorphosis) {
      ctx.translate(logoElement.x + logoElement.width / 2, logoElement.y + logoElement.height / 2)
      ctx.scale(1, 0.5)
      ctx.drawImage(logoElement.image, -logoElement.width / 2, -logoElement.height / 2, logoElement.width, logoElement.height)
    } else {
      ctx.drawImage(logoElement.image, logoElement.x, logoElement.y, logoElement.width, logoElement.height)
    }

    if (showSelection && !isFullscreen && selectedLogoId === logoElement.id) {
      ctx.globalAlpha = 1
      if (enableAnamorphosis) {
        drawSelectionIndicator(ctx, -logoElement.width / 2, -logoElement.height / 2, logoElement.width, logoElement.height, isDragging)
      } else {
        drawSelectionIndicator(ctx, logoElement.x, logoElement.y, logoElement.width, logoElement.height, isDragging)
      }
    }

    ctx.restore()
  })
}

export function renderTextElements(
  ctx: CanvasRenderingContext2D,
  textElements: TextElement[],
  selectedTextId: string | null,
  isFullscreen: boolean,
  isDragging: boolean,
  showSelection: boolean,
  selfTestEnabled: boolean,
  enableAnamorphosis = false
) {
  if (selfTestEnabled) return

  textElements.forEach((textElement) => {
    const fontWeight = textElement.fontWeight.toString()
    ctx.font = `${textElement.fontStyle} ${fontWeight} ${textElement.fontSize}px ${textElement.fontFamily}`
    ctx.fillStyle = textElement.color
    ctx.textAlign = textElement.textAlign as CanvasTextAlign
    ctx.textBaseline = "top"

    ctx.shadowColor = "rgba(0, 0, 0, 0.8)"
    ctx.shadowBlur = 4
    ctx.shadowOffsetX = 2
    ctx.shadowOffsetY = 2

    let textX = textElement.x

    const drawTextAt = (x: number, y: number) => {
      if ((textElement.strokeWidth && textElement.strokeWidth > 0) || textElement.fontWeight > 700) {
        const strokeWidth =
          (textElement.strokeWidth || 0) +
          (textElement.fontWeight > 700 ? Math.floor((textElement.fontWeight - 700) / 200) : 0)
        if (strokeWidth > 0) {
          ctx.strokeStyle = textElement.color
          ctx.lineWidth = strokeWidth
          ctx.strokeText(textElement.text, x, y)
        }
      }

      ctx.fillText(textElement.text, x, y)
    }

    if (enableAnamorphosis) {
      ctx.save()
      ctx.translate(textX, textElement.y + textElement.fontSize / 2)
      ctx.scale(1, 0.5)
      drawTextAt(0, -textElement.fontSize / 2)
      ctx.restore()
    } else {
      drawTextAt(textX, textElement.y)
    }

    if (showSelection && !isFullscreen && selectedTextId === textElement.id) {
      ctx.shadowColor = "transparent"
      ctx.shadowBlur = 0
      ctx.shadowOffsetX = 0
      ctx.shadowOffsetY = 0

      const metrics = ctx.measureText(textElement.text)
      const textWidth = metrics.width
      const textHeight = textElement.fontSize

      let rectX = textX
      if (textElement.textAlign === "center") {
        rectX = textX - textWidth / 2
      } else if (textElement.textAlign === "right") {
        rectX = textX - textWidth
      }

      drawSelectionIndicator(ctx, rectX, textElement.y, textWidth, textHeight, isDragging)
      ctx.fillStyle = textElement.color
    }

    ctx.shadowColor = "transparent"
    ctx.shadowBlur = 0
    ctx.shadowOffsetX = 0
    ctx.shadowOffsetY = 0
  })
}

export function renderResolution(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  config: {
    showResolution: boolean
    resolutionFontSize: number
    ledWallMode?: LedWallMode
    standardPatternType: string
  },
  activePatternType: string,
  gridSize: number,
  enableAnamorphosis = false
) {
  if (!config.showResolution) return

  const fontSize = config.resolutionFontSize || 24
  ctx.fillStyle = "rgba(255, 255, 255, 0.9)"
  ctx.strokeStyle = "rgba(0, 0, 0, 0.8)"
  ctx.lineWidth = Math.max(1, fontSize / 12)
  ctx.font = `bold ${fontSize}px Arial, sans-serif`
  ctx.textAlign = "left"
  ctx.textBaseline = "bottom"

  let resolutionText: string
  const isStandardPatternActive = activePatternType === 'standard' && config.standardPatternType !== 'none'

  if (isStandardPatternActive) {
    resolutionText = `${width} × ${height} px`
  } else if (config.ledWallMode?.enabled) {
    resolutionText = `${width} × ${height} px | ${config.ledWallMode?.panelWidth || 192} × ${config.ledWallMode?.panelHeight || 192}`
  } else {
    resolutionText = `${width} × ${height} px | Grille: ${gridSize}px`
  }

  const textX = 20
  const textY = height - 20

  if (enableAnamorphosis) {
    ctx.save()
    ctx.translate(textX, textY - fontSize / 2)
    ctx.scale(1, 0.5)
    ctx.strokeText(resolutionText, 0, fontSize / 2)
    ctx.fillText(resolutionText, 0, fontSize / 2)
    ctx.restore()
  } else {
    ctx.strokeText(resolutionText, textX, textY)
    ctx.fillText(resolutionText, textX, textY)
  }
}

export function renderDefaultLogo(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  config: {
    showDefaultLogo: boolean
    gridOffset: GridOffset
    pixelMapMode?: PixelMapMode
  },
  defaultLogoRef: HTMLImageElement | null,
  logoElementsCount: number,
  selfTestEnabled: boolean,
  enableAnamorphosis = false
) {
  if (!defaultLogoRef || logoElementsCount > 0 || !config.showDefaultLogo || selfTestEnabled || config.pixelMapMode?.enabled) return

  const logo = defaultLogoRef
  const scale = Math.min(width, height) * 0.3 / Math.max(logo.width, logo.height)
  const logoWidth = logo.width * scale
  const logoHeight = logo.height * scale
  const logoX = (width - logoWidth) / 2 + config.gridOffset.x
  const logoY = (height - logoHeight) / 2 + config.gridOffset.y

  ctx.globalAlpha = 0.95
  if (enableAnamorphosis) {
    ctx.save()
    ctx.translate(logoX + logoWidth / 2, logoY + logoHeight / 2)
    ctx.scale(1, 0.5)
    ctx.drawImage(logo, -logoWidth / 2, -logoHeight / 2, logoWidth, logoHeight)
    ctx.restore()
  } else {
    ctx.drawImage(logo, logoX, logoY, logoWidth, logoHeight)
  }
  ctx.globalAlpha = 1
}

export function renderIconLight(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  config: {
    showDefaultLogo: boolean
    gridOffset: GridOffset
  },
  iconLightRef: HTMLImageElement | null,
  logoElementsCount: number,
  isPro: boolean,
  selfTestEnabled: boolean,
  enableAnamorphosis = false
) {
  const shouldShowIconLight = isPro
    ? false
    : (!config.showDefaultLogo || logoElementsCount > 0)

  if (!iconLightRef || selfTestEnabled || !shouldShowIconLight) return

  const icon = iconLightRef
  const iconSize = Math.min(width, height) * 0.24
  const iconPadding = 20
  const iconX = width - iconSize - iconPadding + config.gridOffset.x
  const iconY = height - iconSize - iconPadding + config.gridOffset.y

  ctx.globalAlpha = 0.9
  if (enableAnamorphosis) {
    ctx.save()
    ctx.translate(iconX + iconSize / 2, iconY + iconSize / 2)
    ctx.scale(1, 0.5)
    ctx.drawImage(icon, -iconSize / 2, -iconSize / 2, iconSize, iconSize)
    ctx.restore()
  } else {
    ctx.drawImage(icon, iconX, iconY, iconSize, iconSize)
  }
  ctx.globalAlpha = 1
}

export function renderPixelMapMode(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  pixelMapMode: PixelMapMode,
  isPro: boolean,
  selfTestEnabled: boolean,
  enableAnamorphosis = false
) {
  if (!isPro || !pixelMapMode.enabled || selfTestEnabled) return

  const pmo = pixelMapMode
  ctx.save()

  ctx.setLineDash([])
  ctx.shadowColor = 'transparent'
  ctx.shadowBlur = 0
  ctx.shadowOffsetX = 0
  ctx.shadowOffsetY = 0

  const lineW = Math.max(1, Math.round(Math.min(width, height) * 0.0015))
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.55)'
  ctx.lineWidth = lineW

  // Dessiner la croix (sans anamorphose)
  ctx.beginPath()
  ctx.moveTo(0, 0)
  ctx.lineTo(width, height)
  ctx.stroke()

  ctx.beginPath()
  ctx.moveTo(width, 0)
  ctx.lineTo(0, height)
  ctx.stroke()

  const centerX = width / 2
  const centerY = height / 2
  const sizeMultiplier = pixelMapMode.size ?? 1.0
  const mainFontSize = Math.round(height * 0.07 * sizeMultiplier)
  const infoFontSize = Math.round(mainFontSize * 0.72)
  const lineGap = Math.round(mainFontSize * 0.8)

  const circleScaleX = enableAnamorphosis ? 2 : 1
  const circleRadius = Math.max(0, Math.min(width / circleScaleX, height) / 2 - lineW / 2)

  // Dessiner le cercle (avec anamorphose spéciale: garde la hauteur mais étire horizontalement)
  if (enableAnamorphosis) {
    ctx.save()
    ctx.translate(centerX, centerY)
    ctx.scale(2, 1) // Étirer horizontalement de 2x pour compenser l'écrasement vertical
    ctx.translate(-centerX, -centerY)
  }
  ctx.beginPath()
  ctx.arc(width / 2, height / 2, circleRadius, 0, Math.PI * 2)
  ctx.stroke()
  if (enableAnamorphosis) {
    ctx.restore()
  }

  // Appliquer l'anamorphose standard sur le texte et les rectangles de fond (écrasement vertical)
  if (enableAnamorphosis) {
    ctx.translate(centerX, centerY)
    ctx.scale(1, 0.5)
    ctx.translate(-centerX, -centerY)
  }

  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'

  const mainText = pmo.mainScreenText.toUpperCase()
  ctx.font = `${mainFontSize}px Impact, sans-serif`
  const mainMetrics = ctx.measureText(mainText)
  const mainPadW = mainFontSize * 0.25
  const mainPadH = mainFontSize * 0.18
  const mainY = centerY - lineGap * 0.55
  ctx.fillStyle = '#000000'
  ctx.fillRect(
    centerX - mainMetrics.width / 2 - mainPadW,
    mainY - mainFontSize * 0.5 - mainPadH,
    mainMetrics.width + mainPadW * 2,
    mainFontSize + mainPadH * 2
  )
  ctx.fillStyle = '#ffffff'
  ctx.fillText(mainText, centerX, mainY)

  const infoText = `${pmo.posX}, ${pmo.posY} // ${pmo.resW} x ${pmo.resH} // ${pmo.panelW}px x ${pmo.panelH}px`
  ctx.font = `${infoFontSize}px Impact, sans-serif`
  const infoY = centerY + lineGap * 0.55
  const infoMetrics = ctx.measureText(infoText)
  const infoPadW = infoFontSize * 0.25
  const infoPadH = infoFontSize * 0.18
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(
    centerX - infoMetrics.width / 2 - infoPadW,
    infoY - infoFontSize * 0.5 - infoPadH,
    infoMetrics.width + infoPadW * 2,
    infoFontSize + infoPadH * 2
  )
  ctx.fillStyle = '#000000'
  ctx.fillText(infoText, centerX, infoY)

  ctx.restore()
}

interface PanelConnection {
  panelId: string;
  port: number;
  order: number;
}

export function renderNovaCustomNumbers(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  config: {
    gridSize: number
    gridOffset: { x: number; y: number }
    ledWallMode?: LedWallMode
  },
  panelConnections: PanelConnection[],
  dragPath?: { col: number; row: number }[],
  showArrows?: boolean,
  showLines?: boolean,
  minimalMode?: boolean
) {
  if (!config.ledWallMode?.enabled) return

  const { columns, rows, panelWidth, panelHeight } = config.ledWallMode
  const offsetX = config.gridOffset.x
  const offsetY = config.gridOffset.y
  const fontSize = Math.max(16, Math.min(panelWidth / 4, panelHeight / 4, 48))
  
  const shouldShowArrows = showArrows !== false
  const shouldShowLines = showLines !== false
  const shouldUseMinimalMode = minimalMode === true

  ctx.font = `bold ${fontSize}px Arial, sans-serif`
  ctx.textAlign = "center"
  ctx.textBaseline = "middle"

  // Afficher le chemin de sélection pendant le drag
  if (dragPath && dragPath.length > 0) {
    // Afficher tous les panneaux du chemin avec un fond semi-transparent
    dragPath.forEach(panel => {
      if (panel.col >= 0 && panel.col < columns && panel.row >= 0 && panel.row < rows) {
        const x = panel.col * panelWidth + offsetX
        const y = panel.row * panelHeight + offsetY
        
        ctx.fillStyle = 'rgba(59, 130, 246, 0.3)'
        ctx.fillRect(x, y, panelWidth, panelHeight)
        
        ctx.strokeStyle = '#3b82f6'
        ctx.lineWidth = 2
        ctx.strokeRect(x, y, panelWidth, panelHeight)
      }
    })
    
    // Dessiner des flèches entre les panneaux pour montrer la direction
    if (shouldShowLines || shouldShowArrows) {
      ctx.strokeStyle = '#3b82f6'
      ctx.fillStyle = '#3b82f6'
      ctx.lineWidth = 6
      
      for (let i = 0; i < dragPath.length - 1; i++) {
        const from = dragPath[i]
        const to = dragPath[i + 1]
        
        if (from.col >= 0 && from.col < columns && from.row >= 0 && from.row < rows &&
            to.col >= 0 && to.col < columns && to.row >= 0 && to.row < rows) {
          const fromX = from.col * panelWidth + panelWidth / 2 + offsetX
          const fromY = from.row * panelHeight + panelHeight / 2 + offsetY
          const toX = to.col * panelWidth + panelWidth / 2 + offsetX
          const toY = to.row * panelHeight + panelHeight / 2 + offsetY
          
          // Dessiner la ligne si activé
          if (shouldShowLines) {
            ctx.beginPath()
            ctx.moveTo(fromX, fromY)
            ctx.lineTo(toX, toY)
            ctx.stroke()
          }
          
          // Dessiner la pointe de flèche si activé
          if (shouldShowArrows) {
            const angle = Math.atan2(toY - fromY, toX - fromX)
            const arrowSize = 20
            const midX = (fromX + toX) / 2
            const midY = (fromY + toY) / 2
            
            // Calculer les 3 sommets du triangle (centre de la flèche au milieu)
            const tipX = midX + (arrowSize / 2) * Math.cos(angle)
            const tipY = midY + (arrowSize / 2) * Math.sin(angle)
            
            const base1X = tipX - arrowSize * Math.cos(angle - Math.PI / 6)
            const base1Y = tipY - arrowSize * Math.sin(angle - Math.PI / 6)
            
            const base2X = tipX - arrowSize * Math.cos(angle + Math.PI / 6)
            const base2Y = tipY - arrowSize * Math.sin(angle + Math.PI / 6)
            
            ctx.beginPath()
            ctx.moveTo(tipX, tipY)
            ctx.lineTo(base1X, base1Y)
            ctx.lineTo(base2X, base2Y)
            ctx.closePath()
            ctx.fill()
          }
        }
      }
    }
  }

  // Afficher les connexions existantes
  if (panelConnections.length > 0) {
    // Couleurs pour les différents ports
    const portColors = [
      '#FACB95', // P1
      '#E46971', // P2
      '#DE202C', // P3
      '#F0A58F', // P4
      '#7648BC', // P5
      '#FB94E2', // P6
      '#F8AC73', // P7
      '#DFDFDF', // P8
      '#DAF163', // P9
      '#E4E888', // P10
      '#8BDAFF', // P11
      '#7CECF0', // P12
      '#5952D9', // P13
      '#3E4B5E', // P14
      '#ACD88A', // P15
      '#74D969', // P16
      '#D2A59C', // P17
      '#EC4D7A', // P18
      '#3E101A', // P19
      '#DD121B', // P20
    ]

    // Grouper les connexions par port
    const connectionsByPort = new Map<number, PanelConnection[]>()
    panelConnections.forEach(conn => {
      if (!connectionsByPort.has(conn.port)) {
        connectionsByPort.set(conn.port, [])
      }
      connectionsByPort.get(conn.port)!.push(conn)
    })

    // Dessiner des flèches entre les connexions pour chaque port
    if (shouldShowLines || shouldShowArrows) {
      connectionsByPort.forEach((connections, port) => {
        const portColor = portColors[(port - 1) % portColors.length]
        
        // Trier les connexions par ordre
        const sortedConnections = [...connections].sort((a, b) => a.order - b.order)
        
        // Dessiner des flèches entre les connexions consécutives
        ctx.strokeStyle = portColor
        ctx.fillStyle = portColor
        ctx.lineWidth = 6
        
        for (let i = 0; i < sortedConnections.length - 1; i++) {
          const from = sortedConnections[i]
          const to = sortedConnections[i + 1]
          
          const [fromColStr, fromRowStr] = from.panelId.split('.')
          const [toColStr, toRowStr] = to.panelId.split('.')
          
          const fromCol = parseInt(fromColStr) - 1
          const fromRow = parseInt(fromRowStr) - 1
          const toCol = parseInt(toColStr) - 1
          const toRow = parseInt(toRowStr) - 1
          
          if (fromCol >= 0 && fromCol < columns && fromRow >= 0 && fromRow < rows &&
              toCol >= 0 && toCol < columns && toRow >= 0 && toRow < rows) {
            const fromX = fromCol * panelWidth + panelWidth / 2 + offsetX
            const fromY = fromRow * panelHeight + panelHeight / 2 + offsetY
            const toX = toCol * panelWidth + panelWidth / 2 + offsetX
            const toY = toRow * panelHeight + panelHeight / 2 + offsetY
            
            // Dessiner la ligne si activé
            if (shouldShowLines) {
              ctx.beginPath()
              ctx.moveTo(fromX, fromY)
              ctx.lineTo(toX, toY)
              ctx.stroke()
            }
            
            // Dessiner la pointe de flèche si activé
            if (shouldShowArrows) {
              const angle = Math.atan2(toY - fromY, toX - fromX)
              const arrowSize = 20
              const midX = (fromX + toX) / 2
              const midY = (fromY + toY) / 2
              
              // Calculer les 3 sommets du triangle (centre de la flèche au milieu)
              const tipX = midX + (arrowSize / 2) * Math.cos(angle)
              const tipY = midY + (arrowSize / 2) * Math.sin(angle)
              
              const base1X = tipX - arrowSize * Math.cos(angle - Math.PI / 6)
              const base1Y = tipY - arrowSize * Math.sin(angle - Math.PI / 6)
              
              const base2X = tipX - arrowSize * Math.cos(angle + Math.PI / 6)
              const base2Y = tipY - arrowSize * Math.sin(angle + Math.PI / 6)
              
              ctx.beginPath()
              ctx.moveTo(tipX, tipY)
              ctx.lineTo(base1X, base1Y)
              ctx.lineTo(base2X, base2Y)
              ctx.closePath()
              ctx.fill()
            }
          }
        }
      })
    }

    // Afficher les numéros personnalisés
    connectionsByPort.forEach((connections, port) => {
      const portColor = portColors[(port - 1) % portColors.length]
      
      // Trier les connexions par ordre pour déterminer les directions des flèches
      const sortedConnections = [...connections].sort((a, b) => a.order - b.order)
      
      // Créer une map pour stocker les directions de flèches pour chaque panneau
      const panelDirections = new Map<string, Set<string>>()
      
      for (let i = 0; i < sortedConnections.length - 1; i++) {
        const from = sortedConnections[i]
        const to = sortedConnections[i + 1]
        
        const [fromColStr, fromRowStr] = from.panelId.split('.')
        const [toColStr, toRowStr] = to.panelId.split('.')
        
        const fromCol = parseInt(fromColStr) - 1
        const fromRow = parseInt(fromRowStr) - 1
        const toCol = parseInt(toColStr) - 1
        const toRow = parseInt(toRowStr) - 1
        
        // Calculer la direction de la flèche
        const deltaX = toCol - fromCol
        const deltaY = toRow - fromRow
        
        // Déterminer le côté de sortie pour le panneau 'from'
        if (!panelDirections.has(from.panelId)) {
          panelDirections.set(from.panelId, new Set())
        }
        
        // Déterminer le côté d'entrée pour le panneau 'to'
        if (!panelDirections.has(to.panelId)) {
          panelDirections.set(to.panelId, new Set())
        }
        
        // Déterminer les côtés basés sur la direction
        if (deltaX > 0) {
          // Flèche vers la droite: sortie droite pour 'from', entrée gauche pour 'to'
          panelDirections.get(from.panelId)!.add('right')
          panelDirections.get(to.panelId)!.add('left')
        } else if (deltaX < 0) {
          // Flèche vers la gauche: sortie gauche pour 'from', entrée droite pour 'to'
          panelDirections.get(from.panelId)!.add('left')
          panelDirections.get(to.panelId)!.add('right')
        } else if (deltaY > 0) {
          // Flèche vers le bas: sortie bas pour 'from', entrée haut pour 'to'
          panelDirections.get(from.panelId)!.add('bottom')
          panelDirections.get(to.panelId)!.add('top')
        } else if (deltaY < 0) {
          // Flèche vers le haut: sortie haut pour 'from', entrée bas pour 'to'
          panelDirections.get(from.panelId)!.add('top')
          panelDirections.get(to.panelId)!.add('bottom')
        }
      }
      
      connections.forEach(conn => {
        const [colStr, rowStr] = conn.panelId.split('.')
        const col = parseInt(colStr) - 1
        const row = parseInt(rowStr) - 1

        if (col >= 0 && col < columns && row >= 0 && row < rows) {
          const x = col * panelWidth + panelWidth / 2 + offsetX
          const y = row * panelHeight + panelHeight / 2 + offsetY
          
          const portNumber = String(port).padStart(2, '0')
          const panelNumber = String(conn.order).padStart(3, '0')
          const displayText = `P:${portNumber}  #${panelNumber}`
          
          ctx.fillStyle = portColor
          ctx.shadowColor = "rgba(0, 0, 0, 0.8)"
          ctx.shadowBlur = 3
          ctx.shadowOffsetX = 2
          ctx.shadowOffsetY = 2
          
          if (shouldUseMinimalMode) {
            const minimalFontSize = Math.max(8, Math.min(panelWidth / 10, panelHeight / 10, 18))
            const inset = Math.max(8, Math.min(panelWidth, panelHeight) * 0.08)
            
            // Obtenir les directions pour ce panneau
            const directions = panelDirections.get(conn.panelId) || new Set()

            // Si aucune direction (panneau seul), afficher sur le côté haut par défaut
            if (directions.size === 0) {
              directions.add('top')
            }

            ctx.save()
            ctx.font = `bold ${minimalFontSize}px Arial, sans-serif`
            ctx.textAlign = "center"
            ctx.textBaseline = "middle"

            // N'écrire que sur les côtés où il y a des flèches (ou haut par défaut si seul)
            if (directions.has('top')) {
              ctx.fillText(displayText, x, row * panelHeight + offsetY + inset)
            }
            if (directions.has('bottom')) {
              ctx.fillText(displayText, x, (row + 1) * panelHeight + offsetY - inset)
            }
            if (directions.has('left')) {
              ctx.translate(col * panelWidth + offsetX + inset, y)
              ctx.rotate(-Math.PI / 2)
              ctx.fillText(displayText, 0, 0)
              ctx.restore()
              ctx.save()
              ctx.font = `bold ${minimalFontSize}px Arial, sans-serif`
              ctx.textAlign = "center"
              ctx.textBaseline = "middle"
              ctx.shadowColor = "rgba(0, 0, 0, 0.8)"
              ctx.shadowBlur = 3
              ctx.shadowOffsetX = 2
              ctx.shadowOffsetY = 2
              ctx.fillStyle = portColor
            }
            if (directions.has('right')) {
              ctx.translate((col + 1) * panelWidth + offsetX - inset, y)
              ctx.rotate(Math.PI / 2)
              ctx.fillText(displayText, 0, 0)
              ctx.restore()
              ctx.save()
              ctx.font = `bold ${minimalFontSize}px Arial, sans-serif`
              ctx.textAlign = "center"
              ctx.textBaseline = "middle"
              ctx.shadowColor = "rgba(0, 0, 0, 0.8)"
              ctx.shadowBlur = 3
              ctx.shadowOffsetX = 2
              ctx.shadowOffsetY = 2
              ctx.fillStyle = portColor
            }
            
            ctx.restore()
          } else {
            const portText = `P:${portNumber}`
            const panelText = `#${panelNumber}`
            ctx.fillText(portText, x, y - fontSize / 2)
            ctx.fillText(panelText, x, y + fontSize / 2)
          }
        }
      })
    })

    ctx.shadowColor = "transparent"
    ctx.shadowBlur = 0
    ctx.shadowOffsetX = 0
    ctx.shadowOffsetY = 0
  }
}
